import * as React from "react";
import { Fab, Typography, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { IconArrowDropDown } from "src/common/icons";
import {
  ITalk,
  ITalkQuestionnaire,
  OpenTasksSubmissionStatus,
  PeerSelectionStatus,
  QuestionnaireType,
  SubmissionStatus,
  TalkStatus
} from "src/common/types";
import { dateWithoutTime, displayDate, getFullName } from "src/helpers";
import DateStatus from "../DateStatus";
import Status from "../EmployeeCard/status";
import Link from "../Link";
import * as qs from "query-string";

const useStyles = makeStyles((theme: any) => ({
  arrow: {
    display: "inline-block" as "inline-block",
    fontSize: "1.35rem",
    lineHeight: "1",
    transform: "rotate(360deg) scale(0.9,1.35)",
    transition: "transform 0.2s linear",
    verticalAlign: "middle" as "middle"
  },
  dateWrapper: {
    color: "#707070",
    display: "none" as "none",
    minWidth: 124,
    padding: "0 8px",
    width: 124,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  expandButton: {
    background: "none" as "none",
    boxShadow: "none" as "none"
  },
  expandButtonLabel: {
    height: "100%"
  },
  expandLabel: {
    alignItems: "center" as "center",
    cursor: "pointer" as "pointer",
    display: "flex" as "flex",
    height: "40px",
    justifyContent: "center" as "center",
    width: "40px"
  },
  expandWrapper: {
    minWidth: 54,
    padding: "0 16px 0 8px",
    width: 54
  },
  feedbackCardContainer: {
    background: "#ffffff",
    border: "1px solid #e6e6e6",
    borderRadius: 4,
    boxShadow: "0 2px 4px 0 rgba(219, 219, 219, 0.5)",
    display: "flex" as "flex",
    flexDirection: "column" as "column",
    margin: "12px 0 0"
  },
  feedbackTalk: {
    display: "flex" as "flex",
    flexDirection: "column" as "column"
  },
  feedbackTalkAction: {
    "& $expandWrapper": {
      display: "none" as "none",
      [theme.breakpoints.up("md")]: {
        display: "block" as "block"
      }
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between" as "space-between",
    lineHeight: "24px",
    padding: "6px 0",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "flex-start" as "flex-start",
      lineHeight: "60px",
      padding: "0"
    }
  },
  feedbackTalkActionName: {
    display: "block" as "block",
    padding: "0 8px 0 16px",
    width: "100%" as "100%",
    [theme.breakpoints.up("md")]: {
      flexGrow: 1,
      padding: "0 8px 0 0",
      width: "auto" as "auto"
    }
  },
  feedbackTalkActions: {
    display: "flex" as "flex",
    flexDirection: "column" as "column",
    maxHeight: 0,
    overflow: "hidden" as "hidden",
    padding: "0",
    transition: ".3s max-height ease-out",
    [theme.breakpoints.up("md")]: {
      padding: "0 0 0 40px"
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0 0 0 85px"
    }
  },
  feedbackTalkCycle: {
    "& $expandWrapper": {
      order: 2,
      [theme.breakpoints.up("md")]: {
        order: "initial" as "initial"
      }
    },
    "& $mobileDates": {
      order: 3
    },
    "& $statusWrapper": {
      order: 4,
      [theme.breakpoints.up("md")]: {
        order: "initial" as "initial"
      }
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between" as "space-between",
    lineHeight: "24px",
    padding: "6px 0",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      lineHeight: "60px",
      padding: "0"
    }
  },
  feedbackTalkCycleName: {
    display: "block" as "block",
    flexGrow: 1,
    padding: "0 8px 0 16px",
    width: "calc(100% - 54px)",
    [theme.breakpoints.up("md")]: {
      width: "auto" as "auto"
    }
  },
  feedbackTalksContainer: {
    display: "flex" as "flex",
    flexDirection: "column" as "column",
    maxHeight: 0,
    overflow: "hidden" as "hidden",
    transition: ".3s max-height ease-out"
  },
  feedbackYearDetails: {
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    lineHeight: "42px",
    [theme.breakpoints.up("md")]: {
      lineHeight: "60px"
    }
  },
  feedbackYearDetailsName: {
    display: "block" as "block",
    flexGrow: 1,
    padding: "0 8px 0 16px"
  },
  feedbackYearDetailsNameLabel: {
    cursor: "pointer" as "pointer"
  },
  mobileDates: {
    display: "block" as "block",
    fontSize: "1.2rem",
    lineHeight: 1,
    maxWidth: "calc(100% - 140px)",
    padding: "0 8px 0 16px",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  respondentWrapper: {
    display: "none" as "none",
    minWidth: 184,
    padding: "0 8px",
    width: 184,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  statusWrapper: {
    display: "block" as "block",
    lineHeight: 1,
    padding: "0 8px",
    [theme.breakpoints.up("md")]: {
      lineHeight: "inherit" as "inherit",
      minWidth: 184,
      width: 184
    }
  },
  title: {
    display: "inline-block" as "inline-block",
    lineHeight: 1,
    overflowWrap: "break-word" as "break-word",
    verticalAlign: "middle" as "middle",
    wordBreak: "break-word" as "break-word"
  },
  toggler: {
    "&:checked ~ $feedbackTalkActions": {
      maxHeight: 999,
      transition: ".3s max-height ease-in"
    },
    "&:checked ~ $feedbackTalkCycle $arrow": {
      transform: "rotate(180deg) scale(0.9,1.35)",
      transition: "transform 0.2s linear"
    },
    "&:checked ~ $feedbackTalksContainer": {
      maxHeight: 9999,
      transition: "1s max-height ease-in"
    },
    "&:checked ~ $feedbackYearDetails $arrow": {
      transform: "rotate(180deg) scale(0.9,1.35)",
      transition: "transform 0.2s linear"
    },
    display: "none" as "none"
  }
}));

interface IOwnProps {
  year: string;
  talks: ITalk[];
  icName: string;
}

const FeedbackCardHistory = ({ talks, year, icName }: IOwnProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const classes = useStyles();

  const currentDate = dateWithoutTime(new Date());
  const yearCheckboxId = `feedbackYear:${year}`;

  return (
    <div className={classes.feedbackCardContainer}>
      <input className={classes.toggler} id={yearCheckboxId} defaultChecked={false} type="checkbox" />
      <div className={classes.feedbackYearDetails}>
        <div className={classes.feedbackYearDetailsName}>
          <label className={classes.feedbackYearDetailsNameLabel} htmlFor={yearCheckboxId}>
            <Typography className={classes.title} display="inline" color="primary" variant="subtitle2">
              {t(`feedback.page.my-feedbacks.feedbackYear`, { year })}
            </Typography>
          </label>
        </div>
        <div className={classes.expandWrapper}>
          <Fab
            classes={{ label: classes.expandButtonLabel }}
            className={classes.expandButton}
            size="small"
            color="default"
          >
            <label className={classes.expandLabel} htmlFor={yearCheckboxId}>
              <Typography color="primary" className={classes.arrow}>
                <IconArrowDropDown />
              </Typography>
            </label>
          </Fab>
        </div>
      </div>
      <div className={classes.feedbackTalksContainer}>
        {talks.map(
          ({
            id: talkId,
            status: talkStatus,
            talkCompletionDueDate,
            talkQuestionnaires,
            participantSignedDate,
            supervisorSignedDate,
            peerSelection,
            cycle: { name: cycleName, id: cycleId }
          }) => {
            const talkCheckboxId = `talk:${talkId}`;

            const {
              firstName: dsvFirstName,
              lastName: dsvLastName,
              middleName: dsvMiddleName
            } = React.useMemo(() => {
              const dsvTemplate = talkQuestionnaires.find(
                ({ questionnaireType }) => questionnaireType === QuestionnaireType.DirectSupervisor
              );

              if (dsvTemplate) {
                const {
                  feedbackGiver: { firstName, lastName, middleName }
                } = dsvTemplate;
                return {
                  firstName,
                  lastName,
                  middleName
                };
              } else {
                return {
                  firstName: "",
                  lastName: "",
                  middleName: ""
                };
              }
            }, [talkQuestionnaires]);

            const dsvFullName = getFullName([dsvFirstName, dsvMiddleName, dsvLastName]);

            const MyQuestionnaireAction = React.useMemo(() => {
              const myQuestionnaire = talkQuestionnaires.find(
                ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
              ) as ITalkQuestionnaire;

              if (myQuestionnaire) {
                const {
                  id: questionnaireId,
                  submissionDueDate: participantsSubmissionDueDate,
                  submissionStatus: participantSubmissionStatus,
                  questionnaireTemplateConfig
                } = myQuestionnaire;
                const participantSubmissionDatePassed = currentDate > dateWithoutTime(participantsSubmissionDueDate);

                const participantDateStatus =
                  participantSubmissionDatePassed && OpenTasksSubmissionStatus.includes(participantSubmissionStatus)
                    ? "warning"
                    : participantSubmissionDatePassed && participantSubmissionStatus === SubmissionStatus.Submitted
                    ? "success"
                    : "normal";
                const { supportedLanguages = null } = questionnaireTemplateConfig
                  ? JSON.parse(questionnaireTemplateConfig)
                  : {};

                return (
                  <div className={classes.feedbackTalkAction}>
                    <div className={classes.feedbackTalkActionName}>
                      <Link
                        to={`/${language}/dashboard/my-feedbacks/my-questionnaire/${questionnaireId}?${btoa(
                          qs.stringify(
                            { supported: [supportedLanguages] },
                            {
                              arrayFormat: "comma"
                            }
                          )
                        )}`}
                      >
                        {t(`feedback.submitter.selfAssessment`)}
                      </Link>
                    </div>
                    <div className={classes.mobileDates}>
                      <Typography variant="body1" display="inline">
                        {`${t(`feedback.page.my-feedbacks.respondent.me`)} `}
                      </Typography>
                      <DateStatus type={participantDateStatus} date={participantsSubmissionDueDate} />
                    </div>
                    <div className={classes.statusWrapper}>
                      <Status type="questionnaire" status={participantSubmissionStatus} />
                    </div>
                    <div className={classes.respondentWrapper}>
                      <Typography className={classes.title} variant="body1" display="inline">
                        {t(`feedback.page.my-feedbacks.respondent.me`)}
                      </Typography>
                    </div>
                    <div className={classes.dateWrapper}>
                      <DateStatus type={participantDateStatus} date={participantsSubmissionDueDate} />
                    </div>
                    <div className={classes.expandWrapper} />
                  </div>
                );
              } else {
                return null;
              }
            }, [talkQuestionnaires, language]);

            const DSVQuestionnaireAction = React.useMemo(() => {
              const supervisorQuestionnaire = talkQuestionnaires.find(
                ({ questionnaireType }) => questionnaireType === QuestionnaireType.DirectSupervisor
              ) as ITalkQuestionnaire;

              if (supervisorQuestionnaire) {
                const { submissionDueDate: supervisorSubmissionDueDate, submissionStatus: supervisorSubmissionStatus } =
                  supervisorQuestionnaire;

                const supervisorSubmissionDatePassed = currentDate > dateWithoutTime(supervisorSubmissionDueDate);

                const supervisorDateStatus =
                  supervisorSubmissionDatePassed && OpenTasksSubmissionStatus.includes(supervisorSubmissionStatus)
                    ? "warning"
                    : supervisorSubmissionDatePassed && supervisorSubmissionStatus === SubmissionStatus.Submitted
                    ? "success"
                    : "normal";

                return (
                  <div className={classes.feedbackTalkAction}>
                    <div className={classes.feedbackTalkActionName}>
                      <Typography className={classes.title} display="inline" variant="subtitle1">
                        {`${t(`feedback.submitter.directSupervisor`)} `}
                      </Typography>
                    </div>
                    <div className={classes.mobileDates}>
                      <Typography variant="body1" display="inline">
                        {`${dsvFullName} `}
                      </Typography>
                      <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate} />
                    </div>
                    <div className={classes.statusWrapper}>
                      <Status type="questionnaire" status={supervisorSubmissionStatus} />
                    </div>
                    <div className={classes.respondentWrapper}>
                      <Typography className={classes.title} variant="body1" display="inline">
                        {dsvFullName}
                      </Typography>
                    </div>
                    <div className={classes.dateWrapper}>
                      <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate} />
                    </div>
                    <div className={classes.expandWrapper} />
                  </div>
                );
              } else {
                return null;
              }
            }, [talkQuestionnaires, language]);

            /**
             * Calculates if the Peer feedback link should be disabled.
             * If the peerselection status is not submitted and the duedate has passsed
             * then the link should be disabled.
             */
            const getPeerSelectionLinkStatus = React.useCallback(
              (peerSelectionStatus: PeerSelectionStatus, peerSelectionPassed: boolean) => {
                if (
                  [PeerSelectionStatus.Cancelled].includes(peerSelectionStatus) ||
                  (![PeerSelectionStatus.Submitted].includes(peerSelectionStatus) && peerSelectionPassed)
                ) {
                  return (
                    <Typography className={classes.title} display="inline" variant="subtitle1">
                      {t(`feedback.submitter.peerSelection`, {})}
                    </Typography>
                  );
                } else {
                  return (
                    <Link to={`/${language}/dashboard/my-feedbacks/peer-selection/${talkId}`}>
                      {t(`feedback.submitter.peerSelection`)}
                    </Link>
                  );
                }
              },
              []
            );

            const PeerSelectionAction = React.useMemo(() => {
              if (peerSelection) {
                const { dueDate: peerSelectionDueDate, status: peerSelectionStatus } = peerSelection;

                const peerSelectionPassed = currentDate > dateWithoutTime(peerSelectionDueDate);

                const peerStatus =
                  peerSelectionPassed && OpenTasksSubmissionStatus.includes(peerSelectionStatus)
                    ? "warning"
                    : peerSelectionPassed && peerSelectionStatus === PeerSelectionStatus.Submitted
                    ? "success"
                    : "normal";

                return (
                  <div className={classes.feedbackTalkAction}>
                    <div className={classes.feedbackTalkActionName}>
                      {getPeerSelectionLinkStatus(peerSelectionStatus, peerSelectionPassed)}
                    </div>
                    <div className={classes.mobileDates}>
                      <Typography variant="body1" display="inline">
                        {`${t(`feedback.page.my-feedbacks.respondent.me`)} `}
                      </Typography>
                      <DateStatus type={peerStatus} date={peerSelectionDueDate} />
                    </div>
                    <div className={classes.statusWrapper}>
                      <Status type="peerSelection" status={peerSelectionStatus} />
                    </div>
                    <div className={classes.respondentWrapper}>
                      <Typography className={classes.title} variant="body1" display="inline">
                        {t(`feedback.page.my-feedbacks.respondent.me`)}
                      </Typography>
                    </div>
                    <div className={classes.dateWrapper}>
                      <DateStatus type={peerStatus} date={peerSelectionDueDate} />
                    </div>
                    <div className={classes.expandWrapper} />
                  </div>
                );
              } else {
                return null;
              }
            }, [peerSelection, language]);

            const TalkSheetAction = React.useMemo(() => {
              const myQuestionnaire = talkQuestionnaires.find(
                ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
              );
              const peerSummaryQuestionnaire = talkQuestionnaires.find(
                ({ questionnaireType }) => questionnaireType === QuestionnaireType.PeerSummary
              );

              const participantQuestionnaire = talkQuestionnaires.find(
                ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
              );

              const completionDatePassed = currentDate > dateWithoutTime(talkCompletionDueDate);

              const talkSignedDate =
                participantSignedDate && supervisorSignedDate
                  ? [participantSignedDate, supervisorSignedDate].sort(
                      (date1, date2) => date2.getTime() - date1.getTime()
                    )[0]
                  : null;

              const talkDateStatus =
                completionDatePassed && OpenTasksSubmissionStatus.includes(talkStatus)
                  ? "warning"
                  : completionDatePassed && talkStatus === TalkStatus.Done
                  ? "success"
                  : "normal";

              const scheduleTalkVisible =
                (myQuestionnaire && myQuestionnaire.submissionStatus === SubmissionStatus.Submitted) ||
                (peerSummaryQuestionnaire && peerSummaryQuestionnaire.submissionStatus === SubmissionStatus.Submitted);

              const { supportedLanguages = null } =
                participantQuestionnaire && participantQuestionnaire.questionnaireTemplateConfig
                  ? JSON.parse(participantQuestionnaire.questionnaireTemplateConfig)
                  : {};
              return (
                <div className={classes.feedbackTalkCycle}>
                  <div className={classes.feedbackTalkCycleName}>
                    {scheduleTalkVisible ? (
                      <Link
                        to={`/${language}/dashboard/my-feedbacks/talk-sheet/${talkId}?${btoa(
                          qs.stringify(
                            { supported: [supportedLanguages] },
                            {
                              arrayFormat: "comma"
                            }
                          )
                        )}`}
                      >
                        {cycleName}
                      </Link>
                    ) : (
                      <Typography className={classes.title} variant="subtitle1" display="inline">
                        {cycleName}
                      </Typography>
                    )}
                  </div>
                  <div className={classes.mobileDates}>
                    {talkStatus === TalkStatus.Done ? (
                      <>
                        {`${displayDate(talkSignedDate as Date)} / `}
                        <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
                      </>
                    ) : (
                      <>
                        {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
                        <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
                      </>
                    )}
                  </div>
                  <div className={classes.statusWrapper}>
                    <Status icName={icName} dsvName={dsvFirstName} type="talk" status={talkStatus} />
                  </div>
                  <div className={classes.respondentWrapper}>
                    <Typography className={classes.title} variant="body1" display="inline">
                      {talkStatus === TalkStatus.WaitingForDSVSignature
                        ? dsvFullName
                        : t(`feedback.page.my-feedbacks.respondent.me`)}
                    </Typography>
                  </div>
                  <div className={classes.dateWrapper}>
                    <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
                  </div>
                  <div className={classes.expandWrapper}>
                    <Fab
                      classes={{ label: classes.expandButtonLabel }}
                      className={classes.expandButton}
                      size="small"
                      color="default"
                    >
                      <label className={classes.expandLabel} htmlFor={talkCheckboxId}>
                        <Typography color="primary" className={classes.arrow}>
                          <IconArrowDropDown />
                        </Typography>
                      </label>
                    </Fab>
                  </div>
                </div>
              );
            }, [talkQuestionnaires, language]);

            return (
              <div key={talkId} className={classes.feedbackTalk}>
                <input id={talkCheckboxId} type="checkbox" className={classes.toggler} />
                {TalkSheetAction}
                <div className={classes.feedbackTalkActions}>
                  {MyQuestionnaireAction}
                  {DSVQuestionnaireAction}
                  {PeerSelectionAction}
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default React.memo(FeedbackCardHistory);
